import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppDispatch, RootState } from '../../store';
import { fetchTemplates } from '../../store/templatesSlice';
import { userChaptersApi, Chapter as ChapterApi } from '../../api';
import { TopNav } from '../../components/TopNav/TopNav';
import { getBookStrings } from '../../data/bookStrings';
import chapterFrame from '../../assets/chapter-frame.png';
import '../../styles/margin.css';
import './Chapter.css';

export const Chapter: React.FC = () => {
  const { templateId, chapterId } = useParams<{ templateId: string; chapterId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { t } = useTranslation('chapter');

  const templates = useSelector((state: RootState) => state.templates.templates);
  const template = templates.find((tpl) => tpl.id === Number(templateId)) || templates[0];
  const bookStrings = getBookStrings(template?.language);

  const [chapters, setChapters] = useState<ChapterApi[]>([]);
  const [name, setName] = useState('');
  const [isHidden, setIsHidden] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const chapterIdNum = chapterId ? Number(chapterId) : NaN;

  useEffect(() => {
    if (!localStorage.getItem('token')) {
      navigate('/login', { replace: true });
      return;
    }
    if (!templateId || !chapterId) navigate('/forms');
  }, [navigate, templateId, chapterId]);

  useEffect(() => {
    if (!templates.length) dispatch(fetchTemplates());
  }, [dispatch, templates.length]);

  useEffect(() => {
    if (!templateId) return;
    userChaptersApi.list(Number(templateId)).then(({ data }) => {
      setChapters(data);
      const cur = data.find((c) => c.id === chapterIdNum);
      if (cur) {
        setName(cur.name || '');
        setIsHidden(!!cur.isHidden);
      }
      setLoaded(true);
    });
  }, [templateId, chapterIdNum]);

  // «Глава N» — позиция среди не-скрытых глав шаблона. Если эта глава
  // скрыта, номер не показываем (в PDF её title-страницы вообще не будет).
  const displayedNumber = useMemo(() => {
    if (isHidden) return 0;
    let n = 0;
    for (const c of chapters) {
      const hidden = c.id === chapterIdNum ? isHidden : !!c.isHidden;
      if (!hidden) {
        n += 1;
        if (c.id === chapterIdNum) return n;
      }
    }
    return 0;
  }, [chapters, chapterIdNum, isHidden]);

  const current = chapters.find((c) => c.id === chapterIdNum);
  const placeholderName = current?.templateName || '';

  // Номер главы для топ-бара — позиция в шаблоне (по sortOrder), стабильна
  // независимо от того, скрыта она или нет. Хочется чтобы заголовок всегда
  // показывал "Глава N".
  const titleNumber = useMemo(() => {
    const idx = chapters.findIndex((c) => c.id === chapterIdNum);
    return idx >= 0 ? idx + 1 : 0;
  }, [chapters, chapterIdNum]);

  const navTitle =
    titleNumber > 0 ? bookStrings.chapterLabel(titleNumber) : '';

  const handleBack = () => navigate('/forms');

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleNameChange = (v: string) => {
    setName(v);
    setHasChanges(true);
  };

  const handleHiddenChange = (v: boolean) => {
    setIsHidden(v);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!chapterId) return;
    setIsSaving(true);
    try {
      // Если поле пустое или совпадает с шаблонным именем — отправляем '',
      // чтобы очистить override и в PDF использовалось шаблонное имя.
      const override =
        name.trim() && name.trim() !== placeholderName ? name.trim() : '';
      await userChaptersApi.patch(Number(chapterId), {
        nameOverride: override,
        isHidden,
      });
      setHasChanges(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2000);
    } catch (err) {
      console.error('Failed to save chapter override:', err);
    } finally {
      setIsSaving(false);
    }
  };

  if (!loaded) {
    return (
      <div className="mg-page mg-ch">
        <TopNav onLogout={handleLogout} back={handleBack} title={navTitle} />
        <main className="mg-ch__main">
          <div className="mg-meta">{t('loading')}</div>
        </main>
      </div>
    );
  }

  if (!current) {
    return (
      <div className="mg-page mg-ch">
        <TopNav onLogout={handleLogout} back={handleBack} title={navTitle} />
        <main className="mg-ch__main">
          <div className="mg-meta">{t('not_found')}</div>
        </main>
      </div>
    );
  }

  const previewName = name.trim() || placeholderName;

  return (
    <div className="mg-page mg-ch">
      <TopNav
        onLogout={handleLogout}
        back={handleBack}
        title={navTitle}
        actions={
          <span className="mg-ch__topnav-actions">
            <span className={`mg-toast ${justSaved ? 'mg-toast--visible' : ''}`}>
              {t('toast_saved')}
            </span>
            <button
              className="mg-btn mg-ch__save"
              onClick={handleSave}
              disabled={isSaving || !hasChanges}
              type="button"
            >
              {isSaving ? t('saving') : t('save')}
            </button>
          </span>
        }
      />

      <main className="mg-ch__main mg-split">
        <section className="mg-ch__stage mg-split__preview">
          <div className={`mg-ch__preview ${isHidden ? 'mg-ch__preview--hidden' : ''}`}>
            <img
              className="mg-ch__frame mg-ch__frame--top"
              src={chapterFrame}
              alt=""
              aria-hidden="true"
            />
            <div className="mg-ch__title">
              {displayedNumber > 0
                ? `${bookStrings.chapterLabel(displayedNumber)}: ${previewName}`
                : previewName}
            </div>
            <img
              className="mg-ch__frame mg-ch__frame--bottom"
              src={chapterFrame}
              alt=""
              aria-hidden="true"
            />
          </div>
          {isHidden && (
            <div className="mg-ch__hidden-note mg-meta">{t('hidden_note')}</div>
          )}
        </section>

        <div className="mg-split__ui">
          <section className="mg-ch__inputs">
            <div className="mg-field">
              <label className="mg-field__label">{t('field_title')}</label>
              <input
                type="text"
                className="mg-input"
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder={placeholderName}
                maxLength={80}
              />
            </div>

            <label className="mg-ch__toggle mg-checkbox">
              <input
                type="checkbox"
                checked={isHidden}
                onChange={(e) => handleHiddenChange(e.target.checked)}
              />
              <span>{t('toggle_hide')}</span>
            </label>
            <p className="mg-ch__hint">{t('toggle_hide_hint')}</p>
          </section>
        </div>
      </main>
    </div>
  );
};
